/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { Button, Icon, Modal } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { URL_MENTIONS } from '@/constants';
import { useOptionsCodeAnts } from '@/hooks';
import type { MentionActe } from '@/types';
import {
  buildMentionPutPayload,
  completerOptionsCodeAnts,
  resolveIdentifiantCodeAnts,
  rtfToText,
} from '@/utils';

import MentionFormFields, { estMentionValide } from './MentionFormFields';
import MODAL_HEADER_STYLE from './modalStyles';

interface ModalModifierMentionProps {
  mention?: Partial<MentionActe> | null;
  onClose: () => void;
  onSaved: () => void;
}

const ModalModifierMention = (props: ModalModifierMentionProps) => {
  const { mention, onClose, onSaved } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);

  const open = !_.isNil(mention);

  const { options: optionsCodeAnts } = useOptionsCodeAnts(open);

  React.useEffect(() => {
    if (_.isNil(mention)) {
      setData({});
      return;
    }

    setData({
      numero: _.isNil(mention?.numero) ? '' : String(mention.numero),
      dateApposition: mention?.dateApposition,
      titre: mention?.titre,
      // Le RTF n'est pas éditable en l'état : on édite sa version lisible, et
      // `buildMentionPutPayload` ne la renvoie que si elle a changé.
      texte: rtfToText(mention?.texte),
      identifiantCodeAnts: resolveIdentifiantCodeAnts(mention) ?? '',
    });
  }, [mention]);

  const optionsCodeAntsAffichees = React.useMemo(
    () =>
      completerOptionsCodeAnts(
        optionsCodeAnts,
        data?.identifiantCodeAnts,
        mention?.libelle,
      ),
    [optionsCodeAnts, data?.identifiantCodeAnts, mention?.libelle],
  );

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleSave = React.useCallback(() => {
    if (_.isNil(mention?.identifiant)) {
      return;
    }

    setSaving(true);
    // Endpoint générique de la mention, et non le PUT du dossier de
    // numérisation : il n'existe pas pour la reconnaissance.
    request
      .put(
        `${URL_MENTIONS}/${mention.identifiant}`,
        buildMentionPutPayload(mention, data),
      )
      .then(() => {
        onSaved();
        onClose();
      })
      .catch(() => {
        toast.error(
          'Une erreur est survenue lors de la modification de la mention',
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [mention, data, onSaved, onClose]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      {/* @ts-ignore */}
      <Modal.Header
        style={MODAL_HEADER_STYLE}
        content={"Modification d'une mention"}
      />
      <Modal.Content>
        <MentionFormFields
          data={data}
          optionsCodeAnts={optionsCodeAntsAffichees}
          onChange={handleChange}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button data-testid="mention-modification-annuler" onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          data-testid="mention-modification-enregistrer"
          loading={saving}
          disabled={!estMentionValide(data) || saving}
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalModifierMention;
